/**
 * 成员管理。
 *
 * 两个刻意的约束（都在后端强制，界面只是把它们说清楚）：
 *
 * - **不能授予高于自己的角色**。admin 给别人发 owner 等于提权。
 *   但管理**下级组织**时不受此限 —— 上级本来就该能指派下级组织的 owner。
 * - **移除成员会立刻踢掉该用户的会话**。少了这一步，被移出组织的人虽然拿不到数据
 *  （每个请求都会 403），但会话能在服务端无限续期，一旦被重新加回就立刻恢复访问。
 *
 * ## 可见范围（平台成员专属）
 *
 * 角色是**纵向**权级（能不能改），可见范围是**横向**资源边界（能看到什么）。
 * 两者正交，所以分成两个入口 —— 把「只读」和「只看这两个通道」混进一个下拉框，
 * 迟早有人把 viewer 当成「安全的」而忘了他其实看得到全平台的钱。
 *
 * 两条互相独立的收窄，都只做减法：
 *
 *   - **通道白名单**（member_channel_scopes）管**哪些行**：只看得到这几条通道上的单
 *   - **看不到钱**（money_hidden）管**哪些列**：同一行，遮掉成本/费率/分润/结算金额
 *
 * 典型组合是两个一起开：一个「只盯这两条通道的交易成没成、结没结」的运营岗。
 */

import { useState } from 'react';
import { request, type Collection } from '../lib/api.ts';
import { useAsync, useMutation } from '../lib/hooks.ts';
import { useSession } from '../lib/session.tsx';
import {
  Badge, Button, Card, CopyId, ErrorBox, Field, Modal, Select, TextInput,
} from '../components/ui.tsx';
import { DataTable, type Column } from '../components/table.tsx';
import { ROLE_LABEL, formatRelative, roleAtLeast } from '../lib/format.ts';
import { NAV_ITEMS } from '../lib/nav.ts';
import type { Channel, ChannelGroup, Member, Org, OrgRole, ScopedChannel } from '../lib/types.ts';

const ROLES: readonly OrgRole[] = ['viewer', 'operator', 'admin', 'owner'];

/**
 * 白名单里可勾选的页面 —— **从 NAV_ITEMS 派生**，不另写一份。
 *
 * 后端按 page_key 放行，前端按同一个 key 画入口，而 key 就是 nav 表的 `path`。
 * 在这里手抄一份清单的话，新增页面时改了 nav 忘了改这里，症状是「这个页面
 * 没法分配给任何人」—— 而没人会想到来这里找原因。
 *
 * `profile` 排除在外：它是改密与登出的入口，永远可用，勾不勾都一样。
 */
const PAGE_CHOICES: readonly { key: string; label: string; group: string }[] =
  NAV_ITEMS.filter((item) => item.path !== 'profile').map((item) => ({
    key: item.path,
    label: item.label,
    group: item.group,
  }));

const ROLE_HINT: Readonly<Record<OrgRole, string>> = {
  viewer: '只读：能看数据，不能改任何东西',
  operator: '操作员：能发起提现，不能审批、不能改配置',
  admin: '管理员：能改配置、下发 MID、定价、审批提现',
  owner: '所有者：管理员的全部权限，且能指派其他 owner',
};

export function MembersPage() {
  const session = useSession();
  const [orgId, setOrgId] = useState(session.me.org.id);
  const [adding, setAdding] = useState(false);
  const [scoping, setScoping] = useState<Member | null>(null);

  const orgs = useAsync<{ data: readonly Org[] }>('/v1/orgs', { query: { limit: 200 } });
  const list = useAsync<Collection<Member>>(`/v1/orgs/${orgId}/members`, {}, [orgId]);

  const isSelfOrg = orgId === session.me.org.id;
  const canManage = session.can('admin');
  const isPlatformOrg =
    (orgs.data?.data ?? []).find((org) => org.id === orgId)?.type === 'platform';

  const remove = useMutation(async (memberId: string) =>
    await request(`/v1/orgs/${orgId}/members/${memberId}`, { method: 'DELETE' }));
  const unlock = useMutation(async (memberId: string) =>
    await request(`/v1/orgs/${orgId}/members/${memberId}/unlock`, { method: 'POST' }));
  const resetMfa = useMutation(async (memberId: string) =>
    await request(`/v1/orgs/${orgId}/members/${memberId}/reset-mfa`, { method: 'POST' }));

  const columns: readonly Column<Member>[] = [
    { key: 'email', header: '邮箱', render: (row) => (
      <div className="stack tight">
        <span className="cell-strong">{row.email}</span>
        <CopyId id={row.user_id} />
      </div>
    ) },
    { key: 'role', header: '角色', render: (row) => (
      <Badge tone={row.role === 'owner' ? 'info' : row.role === 'admin' ? 'warn' : 'neutral'}>
        {ROLE_LABEL[row.role]}
      </Badge>
    ) },
    { key: 'status', header: '账号状态', render: (row) => (
      <Badge tone={row.user_status === 'active' ? 'good' : row.user_status === 'locked' ? 'bad' : 'neutral'}>
        {row.user_status === 'active' ? '正常' : row.user_status === 'locked' ? '已锁定' : row.user_status}
      </Badge>
    ) },
    { key: 'login', header: '最后登录', render: (row) => (
      <span className="cell-muted">{formatRelative(row.last_login_at)}</span>
    ) },
    // 可见范围只对平台成员有意义 —— 代理与商户能看到什么由 grants / bindings 决定，
    // 再叠一层白名单只会让人以为自己配了什么其实没配。
    ...(isPlatformOrg ? [{
      key: 'scope',
      header: '可见范围',
      render: (row: Member) => (
        <div className="row tight">
          {row.money_hidden && <Badge tone="warn">看不到钱</Badge>}
          {!row.money_hidden && <span className="cell-muted">不受限</span>}
        </div>
      ),
    }] : []),
    ...(canManage ? [{
      key: 'actions',
      header: '',
      render: (row: Member) => (
        <div className="row tight">
          {row.user_status === 'locked' && (
            <Button size="sm" disabled={unlock.pending} onClick={() => {
              void unlock.run(row.id).then((result) => { if (result !== null) list.reload(); });
            }}>解锁</Button>
          )}
          {isPlatformOrg && row.role !== 'owner' && (
            <Button size="sm" onClick={() => { setScoping(row); }}>可见范围</Button>
          )}
          <Button size="sm" disabled={resetMfa.pending} onClick={() => {
            void resetMfa.run(row.id).then((result) => { if (result !== null) list.reload(); });
          }}>重置二次验证</Button>
          {row.user_id !== session.me.user.id && (
            <Button size="sm" variant="danger" disabled={remove.pending} onClick={() => {
              void remove.run(row.id).then((result) => { if (result !== null) list.reload(); });
            }}>移除</Button>
          )}
        </div>
      ),
    }] : []),
  ];

  return (
    <>
      <div className="row">
        <h1>成员</h1>
        <span style={{ flex: 1 }} />
        {canManage && <Button variant="primary" onClick={() => { setAdding(true); }}>添加成员</Button>}
      </div>

      <ErrorBox error={remove.error} />
      <ErrorBox error={unlock.error} />
      <ErrorBox error={resetMfa.error} />

      <div className="filters">
        <Field label="组织" hint="可以管理自己和下级组织的成员">
          <Select
            value={orgId}
            onChange={setOrgId}
            options={(orgs.data?.data ?? []).map((org) => ({
              value: org.id,
              label: org.id === session.me.org.id ? `${org.name}（本组织）` : org.name,
            }))}
          />
        </Field>
      </div>

      <Card flush>
        <DataTable
          columns={columns}
          rows={list.data?.data ?? []}
          loading={list.loading}
          error={list.error}
          rowKey={(row) => row.id}
          empty="该组织还没有成员"
        />
      </Card>

      <div className="alert" data-tone="info">
        <span className="alert-title">移除成员会立刻踢掉该用户的会话</span>
        <span>
          只删成员行是不够的：被移出的人虽然拿不到数据，但会话能在服务端无限续期，
          一旦被重新加回任意组织就立刻恢复访问。所以删成员必须等于踢会话。
        </span>
      </div>

      {scoping !== null && (
        <VisibilityModal
          orgId={orgId}
          member={scoping}
          onClose={() => { setScoping(null); }}
          onDone={() => { setScoping(null); list.reload(); }}
        />
      )}

      {adding && (
        <AddMemberModal
          orgId={orgId}
          isSelfOrg={isSelfOrg}
          onClose={() => { setAdding(false); }}
          onDone={() => { setAdding(false); list.reload(); }}
        />
      )}
    </>
  );
}

function AddMemberModal({
  orgId, isSelfOrg, onClose, onDone,
}: {
  orgId: string;
  isSelfOrg: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const session = useSession();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgRole>('viewer');
  const [password, setPassword] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);

  const add = useMutation(async () => await request<Member>(`/v1/orgs/${orgId}/members`, {
    method: 'POST',
    body: {
      email,
      role,
      ...(needsPassword ? { password } : {}),
    },
  }));

  // 给本组织加人时不能授予高于自己的角色；管理下级组织时不受此限
  const grantableRoles = isSelfOrg
    ? ROLES.filter((item) => roleAtLeast(session.me.role, item))
    : ROLES;

  return (
    <Modal
      title="添加成员"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            disabled={add.pending || email === '' || (needsPassword && password.length < 12)}
            onClick={() => { void add.run().then((result) => { if (result !== null) onDone(); }); }}
          >
            {add.pending ? '添加中…' : '添加'}
          </Button>
        </>
      }
    >
      <ErrorBox error={add.error} />

      <Field label="邮箱" hint="已注册的用户会直接加入；未注册的需要同时设置初始口令。">
        <TextInput value={email} onChange={setEmail} type="email" />
      </Field>

      <Field label="角色" hint={ROLE_HINT[role]}>
        <Select
          value={role}
          onChange={(value) => { setRole(value as OrgRole); }}
          options={grantableRoles.map((value) => ({ value, label: ROLE_LABEL[value] }))}
        />
      </Field>

      {isSelfOrg && !roleAtLeast(session.me.role, 'owner') && (
        <div className="alert" data-tone="info">
          <span className="alert-title">你不能授予高于自己的角色</span>
          <span>你当前是 {ROLE_LABEL[session.me.role]}，给本组织加人时最高只能授予同级角色。</span>
        </div>
      )}

      <label className="checkbox">
        <input type="checkbox" checked={needsPassword} onChange={(e) => { setNeedsPassword(e.target.checked); }} />
        该邮箱尚未注册，需要为它创建账号
      </label>

      {needsPassword && (
        <Field label="初始口令" hint="至少 12 位。添加后请让对方立即修改。">
          <TextInput value={password} onChange={setPassword} type="password" />
        </Field>
      )}
    </Modal>
  );
}

/**
 * 可见范围：通道白名单 + 金额可见性。
 *
 * 两条独立的收窄放在同一个弹窗里，是因为它们在配置时几乎总是一起考虑
 * （「他只管这两条通道，而且不该看到利润」），但**提交是两个独立请求** ——
 * 后端也是两个接口，一个失败不该把另一个回滚掉，那会让人以为两件事是原子的。
 * 所以哪个成了就显示哪个成了，错误分别报。
 */
function VisibilityModal({
  orgId, member, onClose, onDone,
}: {
  orgId: string;
  member: Member;
  onClose: () => void;
  onDone: () => void;
}) {
  const channels = useAsync<Collection<Channel>>('/v1/channels', { query: { limit: 200 } });
  const currentPages = useAsync<{ data: readonly string[]; unrestricted: boolean }>(
    `/v1/orgs/${orgId}/members/${member.id}/page-scope`, {}, [orgId, member.id],
  );
  const current = useAsync<{
    configured: readonly ScopedChannel[];
    groups: readonly { channel_group_id: string; name: string }[];
    effective: readonly {
      channel_account_id: string;
      label: string;
      channel_code: string;
      source: 'group' | 'direct' | 'both';
      via_groups: readonly string[];
    }[];
    unrestricted: boolean;
    data: readonly ScopedChannel[];
  }>(`/v1/orgs/${orgId}/members/${member.id}/channel-scope`, {}, [orgId, member.id]);
  // 空 deps：分组清单与本弹窗的编辑状态无关
  const allGroups = useAsync<{ data: readonly ChannelGroup[] }>('/v1/channel-groups', {}, []);

  const [picked, setPicked] = useState<readonly string[] | null>(null);
  const [pickedGroups, setPickedGroups] = useState<readonly string[] | null>(null);
  const [moneyHidden, setMoneyHidden] = useState(member.money_hidden);
  const [pages, setPages] = useState<readonly string[] | null>(null);

  // 服务端那份到了之后作为初值；之后以本地编辑为准（null 表示还没接管）
  const selected = picked ?? (current.data?.configured ?? []).map((ch) => ch.id);
  const toggle = (id: string) => {
    setPicked(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  const selectedGroups =
    pickedGroups ?? (current.data?.groups ?? []).map((g) => g.channel_group_id);
  const toggleGroup = (id: string) => {
    setPickedGroups(
      selectedGroups.includes(id)
        ? selectedGroups.filter((x) => x !== id)
        : [...selectedGroups, id],
    );
  };

  // 服务端那份到了之后作为初值；null 表示本地还没接管
  const selectedPages = pages ?? currentPages.data?.data ?? [];
  const togglePage = (key: string) => {
    setPages(
      selectedPages.includes(key)
        ? selectedPages.filter((x) => x !== key)
        : [...selectedPages, key],
    );
  };

  const save = useMutation(async () => {
    // 顺序无关，但分开发：两个维度是独立的，一个 400 不该让另一个也不生效
    await request(`/v1/orgs/${orgId}/members/${member.id}/channel-scope`, {
      method: 'PUT',
      body: { channel_account_ids: selected },
    });
    await request(`/v1/orgs/${orgId}/members/${member.id}/channel-group-scope`, {
      method: 'PUT',
      body: { channel_group_ids: selectedGroups },
    });
    await request(`/v1/orgs/${orgId}/members/${member.id}/money-visibility`, {
      method: 'PUT',
      body: { money_hidden: moneyHidden },
    });
    await request(`/v1/orgs/${orgId}/members/${member.id}/page-scope`, {
      method: 'PUT',
      body: { page_keys: selectedPages },
    });
    return true;
  });

  const pagesDirty =
    pages !== null &&
    (pages.length !== (currentPages.data?.data.length ?? 0) ||
      pages.some((k) => !(currentPages.data?.data ?? []).includes(k)));

  const groupsDirty =
    pickedGroups !== null &&
    (pickedGroups.length !== (current.data?.groups.length ?? 0) ||
      pickedGroups.some(
        (id) => !(current.data?.groups ?? []).some((g) => g.channel_group_id === id),
      ));

  const dirty =
    pagesDirty ||
    groupsDirty ||
    moneyHidden !== member.money_hidden ||
    (picked !== null &&
      (picked.length !== (current.data?.configured.length ?? 0) ||
        picked.some((id) => !(current.data?.configured ?? []).some((ch) => ch.id === id))));

  return (
    <Modal
      title={`可见范围 · ${member.email}`}
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            disabled={save.pending || !dirty}
            onClick={() => { void save.run().then((r) => { if (r !== null) onDone(); }); }}
          >保存</Button>
        </>
      }
    >
      <ErrorBox error={save.error} />
      <ErrorBox error={current.error} />

      {/*
        * 分组优先于逐条勾选呈现：它才是日常该用的配置方式。
        * 配了组，以后这个组里新增的通道他自动看得见，不用再回来改。
        */}
      <Field
        label="通道分组"
        hint="配了分组，以后加进这个分组的通道他会自动看得见，不用再来改一次。"
      >
        <div className="stack tight">
          {(allGroups.data?.data ?? []).map((g) => (
            <label className="checkbox" key={g.id}>
              <input
                type="checkbox"
                checked={selectedGroups.includes(g.id)}
                onChange={() => { toggleGroup(g.id); }}
              />
              {g.name}
              <span className="cell-muted">
                {' · '}
                {g.channel_count === 0 ? '空组（配了也看不到任何通道）' : `${String(g.channel_count)} 条通道`}
              </span>
            </label>
          ))}
          {allGroups.loading && <span className="cell-muted">加载中…</span>}
        </div>
      </Field>

      <Field
        label="单独勾选的通道"
        hint="分组之外的例外出口。与分组取并集 —— 两边都不配才是「不受通道限制」。"
      >
        <div className="stack tight">
          {(channels.data?.data ?? []).map((ch) => (
            <label className="checkbox" key={ch.id}>
              <input
                type="checkbox"
                checked={selected.includes(ch.id)}
                onChange={() => { toggle(ch.id); }}
              />
              {ch.label}
              <span className="cell-muted"> · {ch.channel_code} · {ch.mode} · {ch.channel_group_name}</span>
            </label>
          ))}
          {channels.loading && <span className="cell-muted">加载中…</span>}
        </div>
      </Field>

      {/*
        * 实际生效的清单，带来源。
        *
        * 并集模型的全部代价就集中在可解释性上：一个人能看见某条通道可能有两个原因。
        * 不标出来，排查时就会出现「我把他从组里移出去了，他怎么还看得见」——
        * 而真正原因是他还有一条单独勾选。这一块就是为了让那句话当场有答案。
        */}
      {current.data !== null && !current.data.unrestricted && (
        <Field label="实际生效" hint="分组与单独勾选合并后的结果。括号里是来源。">
          <div className="stack tight">
            {current.data.effective.length === 0 && (
              <span className="cell-muted">
                一条通道都看不到 —— 他配了分组，但那些分组里还没有通道。
                这不是「不受限」，是「受限且看不到任何东西」。
              </span>
            )}
            {current.data.effective.map((row) => (
              <span key={row.channel_account_id}>
                {row.label}
                <span className="cell-muted">
                  {' · '}
                  {row.source === 'both'
                    ? `分组（${row.via_groups.join('、')}）+ 单独勾选`
                    : row.source === 'group'
                      ? `来自分组 ${row.via_groups.join('、')}`
                      : '单独勾选'}
                </span>
              </span>
            ))}
          </div>
        </Field>
      )}

      <Field
        label="金额可见性"
        hint="遮的是「赚了多少」，不是「收了多少」：交易金额与成交量照常可见。"
      >
        <label className="checkbox">
          <input
            type="checkbox"
            checked={moneyHidden}
            onChange={(e) => { setMoneyHidden(e.target.checked); }}
          />
          看不到成本、费率、分润与结算金额
        </label>
      </Field>

      <Field
        label="可见页面"
        hint="一个都不勾 = 不受页面限制（看得到角色允许的全部页面），不是「什么都看不到」。「我的账号」永远保留。"
      >
        <div className="stack tight">
          {PAGE_CHOICES.map((page) => (
            <label className="checkbox" key={page.key}>
              <input
                type="checkbox"
                checked={selectedPages.includes(page.key)}
                onChange={() => { togglePage(page.key); }}
              />
              {page.label}
              <span className="cell-muted"> · {page.group}</span>
            </label>
          ))}
        </div>
      </Field>

      <div className="alert" data-tone="warn">
        <span className="alert-title">页面白名单与上面两条方向相反</span>
        <span>
          通道白名单和「看不到钱」是**减法**：默认看得到，命中才藏。页面白名单是
          **加法**：只看得到勾上的，**以后新增的页面对他默认不可见**。
          这是刻意的 —— 对一个只该看几样东西的账号，「默认可见、逐个拉黑」意味着
          每加一个新页面都要有人记得回来拉黑，而漏掉的那次没有任何症状。
        </span>
      </div>

      <div className="alert" data-tone="info">
        <span className="alert-title">这两条都只做减法</span>
        <span>
          它们在原有的组织树与角色判定之上**再收窄**，不会让任何人看到本来看不到的东西。
          勾上「看不到钱」之后，费率、账本、结算批次、提现四个页面对他直接关闭；
          交易列表仍然能回答「这笔成没成、结没结到银行、哪个批次结的」——
          只是结算金额那一栏是空的（否则交易金额减结算金额就是手续费）。
        </span>
      </div>
    </Modal>
  );
}
